import { Injectable } from '@angular/core';
import { WrapperHttpService } from '../../../../services/wrapper-http-service';
import { API_URL } from '../../../../../services/config';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AllocationDriverViewProfile, AllocationViewProfile, AllocationViewProfileDto } from '../models';
import { AllocationViewProfilesEndpoint } from '../constants';

@Injectable({ providedIn: 'root' })
export class AllocationViewProfileItemService {
  constructor(private http: WrapperHttpService) {}

  getProfile(id: number): Observable<AllocationViewProfile> {
    const url = `${API_URL}${AllocationViewProfilesEndpoint.ViewProfiles}/${id}`;
    return this.http.withLoading().get<AllocationViewProfileDto>(url).pipe(map(this.mapProfileResponse));
  }

  getDefaultProfile(): Observable<AllocationViewProfile> {
    const url = `${API_URL}${AllocationViewProfilesEndpoint.ViewProfileDefault}`;
    return this.http.withLoading().get<AllocationViewProfileDto>(url).pipe(map(this.mapProfileResponse));
  }

  saveProfile(profile: AllocationViewProfile): Observable<AllocationViewProfile> {
    const url = `${API_URL}${AllocationViewProfilesEndpoint.ViewProfiles}`;
    const request = this.mapProfileRequest(profile);

    return (
      profile.id
        ? this.http.withLoading().put<AllocationViewProfileDto | void>(url, request)
        : this.http.withLoading().post<AllocationViewProfileDto>(url, request)
    ).pipe(map((dto: AllocationViewProfileDto | void) => (dto ? this.mapProfileResponse(dto) : profile)));
  }

  deleteProfile(id: number): Observable<void> {
    const url = `${API_URL}${AllocationViewProfilesEndpoint.ViewProfiles}/${id}`;
    return this.http.withLoading().delete<void>(url);
  }

  getDriverViewProfile(id: number): Observable<AllocationDriverViewProfile> {
    const url = `${API_URL}${AllocationViewProfilesEndpoint.AllocationPublic}${AllocationViewProfilesEndpoint.DriverView}${AllocationViewProfilesEndpoint.Profile}/${id}`;
    return this.http.withLoading().get<AllocationDriverViewProfile>(url);
  }

  private mapProfileResponse(dto: AllocationViewProfileDto): AllocationViewProfile {
    return new AllocationViewProfile({
      ...dto,
      id: dto?.profileId ?? dto?.id,
      terminalViewLayout: AllocationViewProfileItemService.parseLayout(dto?.terminalViewLayout),
      calendarViewLayout: AllocationViewProfileItemService.parseLayout(dto?.calendarViewLayout),
    } as AllocationViewProfile);
  }

  private mapProfileRequest(profile: AllocationViewProfile): AllocationViewProfileDto {
    return {
      id: profile.id,
      name: profile.name,
      createdAt: profile.createdAt,
      defaultProfile: profile.defaultProfile,
      terminalViewLayout: profile.terminalViewLayout ?? [],
      calendarViewLayout: profile.calendarViewLayout ?? [],
      highlightOutbound: profile.highlightOutbound,
      scrollingFactor: profile.scrollingFactor,
      driverViewHubId: profile.driverViewHubId,
      driverViewDisplayedTiles: profile.driverViewDisplayedTiles,
      driverViewDarkMode: profile.driverViewDarkMode,
      driverViewIncludeTimeDisplay: profile.driverViewIncludeTimeDisplay,
      driverViewDisplayLanguages: profile.driverViewDisplayLanguages,
    };
  }

  private static parseLayout(layout: string | string[]): string[] {
    if (Array.isArray(layout)) {
      return layout;
    }

    if (!layout) {
      return [];
    }

    try {
      const parsedLayout = JSON.parse(layout);
      return Array.isArray(parsedLayout) ? parsedLayout : [];
    } catch {
      return layout
        .split(',')
        .map((item: string) => item.trim())
        .filter(Boolean);
    }
  }
}
